import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const register = (username, password, role = 'Staff') =>
  api.post('/auth/register', { username, password, role });

export const login = (username, password) =>
  api.post('/auth/login', { username, password });

export const getMe = () => api.get('/auth/me');

// Orders
export const getNextIndentNumber = () => api.get('/orders/next-indent-number');

export const createOrder = (orderData) => api.post('/orders', orderData);

export const getOrders = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.get(`/orders?${params.toString()}`);
};

export const getOrder = (orderId) => api.get(`/orders/${orderId}`);

export const updateOrder = (orderId, orderData) =>
  api.put(`/orders/${orderId}`, orderData);

export const deleteOrder = (orderId) => api.delete(`/orders/${orderId}`);

export const getRecycleBinOrders = () => api.get('/orders/recycle-bin');
export const restoreOrder = (orderId) => api.post(`/orders/${orderId}/restore`);
export const permanentlyDeleteOrder = (orderId) => api.delete(`/orders/${orderId}/permanent`);

export const addDelivery = (orderId, deliveryData) =>
  api.post(`/orders/${orderId}/deliveries`, deliveryData);

export const updateDelivery = (orderId, deliveryId, deliveryData) =>
  api.put(`/orders/${orderId}/deliveries/${deliveryId}`, deliveryData);

export const deleteDelivery = (orderId, deliveryId) =>
  api.delete(`/orders/${orderId}/deliveries/${deliveryId}`);

export const cancelDelivery = (orderId, deliveryId) =>
  api.delete(`/orders/${orderId}/deliveries/${deliveryId}/cancel`);

// Master Data (Controlled Vocabulary)
export const getMasterData = (category, fabricType = null, activeOnly = false) => {
  const params = new URLSearchParams();
  if (fabricType) params.append('fabric_type', fabricType);
  if (activeOnly) params.append('active_only', 'true');
  const queryString = params.toString();
  return api.get(`/master-data/${category}${queryString ? '?' + queryString : ''}`);
};

export const addMasterData = (category, data) =>
  api.post(`/master-data/${category}`, data);

export const updateMasterData = (category, itemId, data) =>
  api.put(`/master-data/${category}/${itemId}`, data);

export const deleteMasterData = (category, itemId) =>
  api.delete(`/master-data/${category}/${itemId}`);

// Fabric Types Configuration
export const getFabricTypes = () => api.get('/config/fabric-types');
export const addFabricType = (fabricType) => api.post('/config/fabric-types', { fabric_type: fabricType });

// Master Data Audit Logs
export const getMasterDataAuditLogs = (category = null, itemId = null, limit = 100) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (itemId) params.append('item_id', itemId);
  if (limit) params.append('limit', limit);
  const queryString = params.toString();
  return api.get(`/master-data-audit-logs${queryString ? '?' + queryString : ''}`);
};

// Transporters
export const getTransporters = (activeOnly = false) =>
  api.get(`/transporters?active_only=${activeOnly}`);

export const createTransporter = (data) =>
  api.post('/transporters', data);

export const updateTransporter = (transporterId, data) =>
  api.put(`/transporters/${transporterId}`, data);

export const deleteTransporter = (transporterId) =>
  api.delete(`/transporters/${transporterId}`);

// Sample Master
export const getSamples = () => api.get('/samples');

export const getSample = (sampleId) => api.get(`/samples/${sampleId}`);

export const getNextSampleId = () => api.get('/samples/next-id');

export const analyzeSamplePhotos = (photos, photoPaths = []) => 
  api.post('/samples/analyze', { photos, photo_paths: photoPaths });

export const createSampleV2 = (data) => api.post('/samples/v2', data);

export const createSample = (data) => api.post('/samples', data);

export const updateSample = (sampleId, data) => api.put(`/samples/${sampleId}`, data);

export const deleteSample = (sampleId) => api.delete(`/samples/${sampleId}`);

export const uploadSamplePhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/samples/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Supplier Identification
export const getSupplierIdentification = (supplierId) =>
  api.get(`/suppliers/${supplierId}/identification`);

export const updateSupplierIdentification = (supplierId, data) =>
  api.put(`/suppliers/${supplierId}/identification`, data);

export const uploadSupplierPhoto = (supplierId, photoType, file) => {
  const formData = new FormData();
  formData.append('photo_type', photoType);
  formData.append('file', file);
  return api.post(`/suppliers/${supplierId}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deleteSupplierPhoto = (supplierId, photoType, photoUrl) =>
  api.delete(`/suppliers/${supplierId}/photos/${photoType}?photo_url=${encodeURIComponent(photoUrl)}`);

// Bale Validation
export const validateBales = (bales, deliveryId = null) =>
  api.post('/orders/validate-bales', bales, { params: { delivery_id: deliveryId } });

// Audit Logs
export const getAuditLogs = (orderId = null, entityType = null, limit = 100) => {
  const params = new URLSearchParams();
  if (orderId) params.append('order_id', orderId);
  if (entityType) params.append('entity_type', entityType);
  params.append('limit', limit);
  return api.get(`/audit-logs?${params.toString()}`);
};

export const getOrderAuditLogs = (orderId) =>
  api.get(`/orders/${orderId}/audit-logs`);

// Saved Values
export const getSavedValues = (category) =>
  api.get(`/saved-values/${category}`);

export const createSavedValue = (category, value) =>
  api.post('/saved-values', { category, value });

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

export const getRecentOrders = () => api.get('/dashboard/recent-orders');

// PDF Export
export const exportOrderPDF = (orderId, options) =>
  api.post(`/orders/${orderId}/export-pdf`, options, {
    responseType: 'blob',
  });

export const exportBuyerPDF = (orderId) =>
  api.post(`/orders/${orderId}/export-pdf/buyer`, {}, {
    responseType: 'blob',
  });

export const exportSupplierPDF = (orderId) =>
  api.post(`/orders/${orderId}/export-pdf/supplier`, {}, {
    responseType: 'blob',
  });

// ==================== FIELD ACTIVITY ====================
// Staff Master
export const getStaffMembers = (activeOnly = false) =>
  api.get(`/field-activity/staff?active_only=${activeOnly}`);

export const createStaffMember = (data) =>
  api.post('/field-activity/staff', data);

export const updateStaffMember = (staffId, data) =>
  api.put(`/field-activity/staff/${staffId}`, data);

export const deleteStaffMember = (staffId) =>
  api.delete(`/field-activity/staff/${staffId}`);

// Field Visits
export const getFieldVisits = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.get(`/field-activity/visits?${params.toString()}`);
};

export const getFieldVisit = (visitId) =>
  api.get(`/field-activity/visits/${visitId}`);

export const createFieldVisit = (data) =>
  api.post('/field-activity/visits', data);

export const updateFieldVisit = (visitId, data) =>
  api.put(`/field-activity/visits/${visitId}`, data);

export const deleteFieldVisit = (visitId) =>
  api.delete(`/field-activity/visits/${visitId}`);

export const getFieldVisitGroups = () =>
  api.get('/field-activity/visit-groups');

export const getFieldVisitGroup = (groupId) =>
  api.get(`/field-activity/visit-groups/${groupId}`);

export const createFieldVisitGroup = (data) =>
  api.post('/field-activity/visit-groups', data);

export const updateFieldVisitGroup = (groupId, data) =>
  api.put(`/field-activity/visit-groups/${groupId}`, data);

export const deleteFieldVisitGroup = (groupId) =>
  api.delete(`/field-activity/visit-groups/${groupId}`);

// Field Tasks
export const getFieldTasks = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.get(`/field-activity/tasks?${params.toString()}`);
};

export const getFieldTask = (taskId) =>
  api.get(`/field-activity/tasks/${taskId}`);

export const createFieldTask = (data) =>
  api.post('/field-activity/tasks', data);

export const updateFieldTask = (taskId, data) =>
  api.put(`/field-activity/tasks/${taskId}`, data);

export const deleteFieldTask = (taskId) =>
  api.delete(`/field-activity/tasks/${taskId}`);

// Field Activity Stats
export const getFieldActivityStats = () =>
  api.get('/field-activity/stats');

// Voice Processing
export const processVoiceInput = (audioBlob, buyerName, options = {}) => {
  const formData = new FormData();
  formData.append('audio_file', audioBlob, 'recording.webm');
  formData.append('buyer_name', buyerName);
  if (options.buyerVisitId) formData.append('buyer_visit_id', options.buyerVisitId);
  return api.post('/field-activity/process-voice', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const appendVisitVoiceUpdate = (visitId, data) =>
  api.post(`/field-activity/visits/${visitId}/voice-updates`, data);

export const saveVoiceCorrection = (data) =>
  api.post('/field-activity/save-voice-correction', data);

export const getVoiceVocabulary = () =>
  api.get('/field-activity/voice-vocabulary');

export const saveVoiceVocabularyBatch = (mappings) =>
  api.post('/field-activity/voice-vocabulary/batch', { mappings });

export const suggestVoiceVocabulary = (data) =>
  api.post('/field-activity/voice-vocabulary/suggestions', data);

export const deleteVoiceVocabulary = (mappingId) =>
  api.delete(`/field-activity/voice-vocabulary/${mappingId}`);

export const getVoiceRecordings = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.get(`/field-activity/voice-recordings?${params.toString()}`);
};

export const getVoiceRecording = (recordingId) =>
  api.get(`/field-activity/voice-recordings/${recordingId}`);

export default api;
